<template>
    <div
        class="etapa-opcao"
        :class="{
            'etapa-opcao--on': escolhido,
            'etapa-opcao--off': !habilitado && !escolhido,
        }"
        role="button"
        tabindex="0"
        @click="$emit('tocar', complemento)"
    >
        <v-img
            v-if="imagem"
            :src="imagem"
            eager
            max-width="46"
            height="46"
            class="etapa-opcao__foto"
        />

        <div class="etapa-opcao__txt">
            <div v-if="chipEstoque" class="etapa-opcao__estoque">{{ chipEstoque }}</div>
            <div class="etapa-opcao__nome">{{ complemento.descricao }}</div>
            <div v-if="complemento.detalhes" class="etapa-opcao__det">{{ complemento.detalhes }}</div>
        </div>

        <span v-if="+complemento.valor" class="etapa-opcao__preco">
            + {{ formatMoney(+complemento.valor) }}
        </span>
        <!--
            Complemento sem valor não mostra nada. "Grátis", e a ausência de preço já diz o mesmo.
            Mantido comentado para voltar fácil se um dia fizer falta.

            <span v-else class="etapa-opcao__gratis">Grátis</span>
        -->

        <span v-if="indisponivel" class="etapa-opcao__esgotado">{{ $t('produto.esgotado') }}</span>

        <span v-else-if="unica" class="etapa-opcao__radio" />

        <span v-else-if="escolhido" class="etapa-contador" @click.stop>
            <v-btn icon small :color="corSobrePrimaria" @click.stop="$emit('remover', complemento)">
                <v-icon>mdi-minus</v-icon>
            </v-btn>
            <input
                v-if="editavel"
                ref="input"
                class="etapa-contador__input"
                :style="{ color: corSobrePrimaria, borderBottomColor: corSobrePrimaria }"
                type="text"
                inputmode="numeric"
                pattern="[0-9]*"
                autocomplete="off"
                maxlength="4"
                :title="$t('etapa.digitarQuantidadeDe', { item: complemento.descricao })"
                :aria-label="$t('etapa.quantidadeDe', { item: complemento.descricao })"
                :value="valorExibido"
                @click.stop
                @focus="aoFocar"
                @keypress="aoTeclar"
                @input="aoDigitar"
                @blur="aoSair"
                @keydown.enter.prevent="confirmar"
                @keydown.esc.prevent="cancelar"
            >
            <span v-else class="etapa-contador__valor" :style="{ color: corSobrePrimaria }">
                {{ complemento.quantidade }}
            </span>
            <v-btn
                icon
                small
                :color="corSobrePrimaria"
                :disabled="!podeIncrementar"
                @click.stop="$emit('somar', complemento)"
            >
                <v-icon>mdi-plus</v-icon>
            </v-btn>
        </span>

        <span v-else class="etapa-opcao__mais">
            <v-icon>mdi-plus</v-icon>
        </span>
    </div>
</template>

<script>
import { corSobre } from '@/utils/utils';
import { escolhaUnica, isIndisponivel, podeSomar, podeTocar } from '@/utils/etapa';

export default {
    name: 'EtapaOpcao',

    props: {
        passo: { type: Object, required: true },
        complemento: { type: Object, required: true },
        chipEstoque: { type: String, default: '' },
        editavel: { type: Boolean, default: false },
    },

    data: () => ({
        rascunho: '',
        focado: false,
    }),

    computed: {
        valorExibido() {
            return this.focado ? this.rascunho : (+this.complemento.quantidade || 0);
        },

        unica() {
            return escolhaUnica(this.passo);
        },

        escolhido() {
            return +this.complemento.quantidade > 0;
        },

        indisponivel() {
            return isIndisponivel(this.complemento);
        },

        habilitado() {
            return podeTocar(this.passo, this.complemento);
        },

        podeIncrementar() {
            return podeSomar(this.passo, this.complemento);
        },

        corSobrePrimaria() {
            return corSobre(this.$vuetify.theme.custom?.primaryColor);
        },

        imagem() {
            const anexos = this.complemento?.imagens;
            const anexo = Array.isArray(anexos) ? anexos[0] : anexos;

            if (!anexo?.url) return null;

            return anexo.tmp
                ? `${this.$urlAPI}/files/tmp/${anexo.url}`
                : `${this.$urlAPI}/storage/${anexo.url}`;
        },
    },

    methods: {
        aoFocar(e) {
            this.rascunho = String(+this.complemento.quantidade || 0);
            this.focado = true;

            this.$nextTick(() => e.target.select());
        },

        aoTeclar(e) {
            e.key?.length === 1 && !/\d/.test(e.key) && e.preventDefault();
        },

        aoDigitar(e) {
            const digitos = e.target.value.replace(/\D/g, '').slice(0, 4);

            this.rascunho = digitos;

            e.target.value !== digitos && this.$nextTick(() => {
                this.$refs.input && (this.$refs.input.value = this.rascunho);
            });
        },

        aoSair() {
            this.focado = false;

            const qtd = +this.rascunho || 0;

            qtd !== (+this.complemento.quantidade || 0)
                && this.$emit('quantidade', { complemento: this.complemento, quantidade: qtd });
        },

        confirmar() {
            this.$refs.input.blur();
        },

        cancelar() {
            this.rascunho = String(+this.complemento.quantidade || 0);
            this.focado = false;
            this.$refs.input.blur();
        },
    },
};
</script>

<style scoped>
.etapa-opcao {
    display: flex;
    align-items: center;
    gap: 11px;
    padding: 9px 12px;
    border: 2px solid rgba(128, 128, 128, 0.25);
    border-radius: 12px;
    cursor: pointer;
    min-height: 62px;
    transition: border-color 0.12s, background 0.12s;
    user-select: none;
}
.etapa-opcao--on {
    border-color: var(--v-primary-base);
    background: rgba(128, 128, 128, 0.07);
}
.etapa-opcao--off { opacity: 0.42; cursor: not-allowed; }

.etapa-opcao__foto { border-radius: 8px; flex: none; }
.etapa-opcao__txt { flex: 1; min-width: 0; }
.etapa-opcao__nome { font-size: 14.5px; font-weight: 600; line-height: 1.25; }
.etapa-opcao__det {
    font-size: 12px;
    color: #8b9098;
    margin-top: 1px;
    overflow-wrap: anywhere;
}
.etapa-opcao__estoque {
    display: inline-block;
    background: #8009e5;
    color: #fff;
    border-radius: 4px;
    padding: 0 6px;
    font-size: 11px;
    line-height: 17px;
    margin-bottom: 3px;
}

.etapa-opcao__preco { font-size: 13.5px; font-weight: 700; color: #0f8a3d; white-space: nowrap; }
/* Par do "Grátis" comentado no template. */
/* .etapa-opcao__gratis { font-size: 12.5px; color: #8b9098; white-space: nowrap; } */
.etapa-opcao__esgotado { font-size: 12px; color: #8b9098; white-space: nowrap; }

.etapa-opcao__radio {
    width: 25px;
    height: 25px;
    border-radius: 50%;
    border: 2px solid rgba(128, 128, 128, 0.45);
    flex: none;
    transition: border 0.12s;
}
.etapa-opcao--on .etapa-opcao__radio { border: 8px solid var(--v-primary-base); }

.etapa-opcao__mais {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 2px solid rgba(128, 128, 128, 0.35);
    flex: none;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #8b9098;
}
.etapa-opcao:hover .etapa-opcao__mais { border-color: var(--v-primary-base); color: var(--v-primary-base); }

.etapa-contador {
    display: flex;
    align-items: center;
    background: var(--v-primary-base);
    border-radius: 22px;
    padding: 2px;
    flex: none;
}
.etapa-contador__valor {
    min-width: 26px;
    text-align: center;
    font-weight: 800;
    font-size: 15px;
}

.etapa-contador__input {
    width: 34px;
    padding: 0;
    border: none;
    border-bottom: 1px dashed;
    background: transparent;
    text-align: center;
    font-weight: 800;
    font-size: 16px;
    cursor: text;
    outline: none;
}
</style>
